/**
 * Helpers for listings that are sold for pick-up on one specific date.
 *
 * These listings use the booking process, but the provider does not manage a schedule.
 * Instead, the pick-up details are saved to listing's publicData:
 * - quantityAvailable: number of items in the lot (maps to booking seats)
 * - pickupDate: 'YYYY-MM-DD' in the time zone of the listing's availability plan
 * - pickupTimeFrom & pickupTimeTo: optional 'HH:mm' strings
 * - pickupDateEnd: end of the pick-up date as Unix timestamp in seconds (used for search)
 *
 * The availability is generated from those: an availability plan without weekly entries
 * and a single availability exception that covers the pick-up date.
 */
import { getStartOf, parseDateFromISO8601, stringifyDateToISO8601 } from './dates';
import { isBookingProcess } from '../transactions/transaction';

const PICKUP_TIME_STEP_IN_MINUTES = 30;
// The search cutoff is rounded so that consecutive search queries can share the same params.
const SEARCH_CUTOFF_ALIGNMENT_IN_SECONDS = 5 * 60;

/**
 * Check if listings of the given transaction process are sold for a single pick-up date.
 *
 * @param {String} processName
 * @returns {boolean} true if the pick-up date flow is in use
 */
export const isPickupDateProcess = processName => isBookingProcess(processName);

/**
 * Check if the listing has the pick-up details that are needed to make it bookable.
 *
 * @param {Object} publicData listing's publicData
 * @returns {boolean} true if both quantity and pick-up date are set
 */
export const hasPickupDetails = publicData => {
  const { pickupDate, quantityAvailable } = publicData || {};
  return !!pickupDate && Number.isInteger(quantityAvailable) && quantityAvailable > 0;
};

/**
 * Options for the pick-up time window selects.
 *
 * @returns {Array<String>} times of day in 'HH:mm' format
 */
export const getPickupTimeOptions = () => {
  const stepsPerDay = (24 * 60) / PICKUP_TIME_STEP_IN_MINUTES;
  return Array.from({ length: stepsPerDay }, (_, i) => {
    const minutes = i * PICKUP_TIME_STEP_IN_MINUTES;
    const hh = `${Math.floor(minutes / 60)}`.padStart(2, '0');
    const mm = `${minutes % 60}`.padStart(2, '0');
    return `${hh}:${mm}`;
  });
};

/**
 * Convert the date picked with a date picker (local time) to 'YYYY-MM-DD' string.
 *
 * @param {Date} date
 * @returns {String} date string
 */
export const pickupDateToString = date => stringifyDateToISO8601(date);

/**
 * Convert 'YYYY-MM-DD' string to a date that can be given to a date picker (local time).
 *
 * @param {String} pickupDate
 * @returns {Date|null} date object
 */
export const pickupDateFromString = pickupDate =>
  pickupDate ? parseDateFromISO8601(pickupDate) : null;

/**
 * Get the pick-up date as an exact time range in the given time zone.
 *
 * @param {String} pickupDate 'YYYY-MM-DD'
 * @param {String} timeZone IANA time zone key
 * @returns {Object} start (inclusive) and end (exclusive) of the pick-up date
 */
export const getPickupDateRange = (pickupDate, timeZone) => {
  const start = parseDateFromISO8601(pickupDate, timeZone);
  const end = getStartOf(start, 'day', timeZone, 1, 'days');
  return { start, end };
};

/**
 * Get the end of the pick-up date as Unix timestamp in seconds.
 *
 * @param {String} pickupDate 'YYYY-MM-DD'
 * @param {String} timeZone IANA time zone key
 * @returns {number} Unix timestamp in seconds
 */
export const getPickupDateEnd = (pickupDate, timeZone) =>
  Math.floor(getPickupDateRange(pickupDate, timeZone).end.getTime() / 1000);

/**
 * Get the time range that an order reserves from the availability of the listing.
 * Every order reserves the whole pick-up date, which means that all the orders
 * of the same listing overlap and reduce the same pool of seats.
 * Note: Marketplace API accepts a booking that has already started, if it has not ended yet.
 * I.e. the items can be ordered on the pick-up date too.
 *
 * @param {String} pickupDate 'YYYY-MM-DD'
 * @param {String} timeZone IANA time zone key
 * @param {Date} [now]
 * @returns {Object|null} start and end of the range or null if the pick-up date has passed
 */
export const getPickupAvailabilityRange = (pickupDate, timeZone, now = new Date()) => {
  const range = getPickupDateRange(pickupDate, timeZone);
  return range.end > now ? range : null;
};

/**
 * Check if the pick-up date of the listing has passed.
 *
 * @param {Object} publicData listing's publicData
 * @param {Date} [now]
 * @returns {boolean} true if the pick-up date is in the past
 */
export const hasPickupDatePassed = (publicData, now = new Date()) => {
  const { pickupDateEnd } = publicData || {};
  return typeof pickupDateEnd === 'number' && pickupDateEnd * 1000 <= now.getTime();
};

/**
 * Search param that leaves out the listings whose pick-up date has passed.
 * Note: this needs a search schema for publicData key 'pickupDateEnd' (type: long).
 *
 * @param {Date} [now]
 * @returns {Object} query param for sdk.listings.query
 */
export const pickupDateSearchParams = (now = new Date()) => {
  const seconds = Math.floor(now.getTime() / 1000);
  const cutoff = seconds - (seconds % SEARCH_CUTOFF_ALIGNMENT_IN_SECONDS);
  return { pub_pickupDateEnd: `${cutoff},` };
};

/**
 * Format 'YYYY-MM-DD' string for the UI. The date is shown as it was saved,
 * i.e. it does not shift according to the time zone of the viewer.
 *
 * @param {String} pickupDate 'YYYY-MM-DD'
 * @param {Object} intl
 * @returns {String} formatted date
 */
export const formatPickupDate = (pickupDate, intl) => {
  const [year, month, day] = pickupDate.split('-').map(Number);
  return intl.formatDate(new Date(Date.UTC(year, month - 1, day, 12)), {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/**
 * Format 'HH:mm' string for the UI.
 *
 * @param {String} time 'HH:mm'
 * @param {Object} intl
 * @returns {String} formatted time
 */
export const formatPickupTime = (time, intl) => {
  const [hours, minutes] = time.split(':').map(Number);
  return intl.formatTime(new Date(Date.UTC(2000, 0, 1, hours, minutes)), {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

/**
 * Format 'HH:mm' string without React Intl, e.g. for data that is saved to the transaction.
 *
 * @param {String} time 'HH:mm'
 * @param {String} locale e.g. 'en-US'
 * @returns {String} formatted time
 */
export const formatPickupTimeForLocale = (time, locale) => {
  const [hours, minutes] = time.split(':').map(Number);
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(2000, 0, 1, hours, minutes)));
};

/**
 * Format the optional pick-up time window for the UI.
 *
 * @param {Object} publicData listing's publicData (or other object with pickupTimeFrom & pickupTimeTo)
 * @param {Object} intl
 * @returns {String|null} formatted time window or null if it is not set
 */
export const formatPickupTimeWindow = (publicData, intl) => {
  const { pickupTimeFrom, pickupTimeTo } = publicData || {};
  const from = pickupTimeFrom ? formatPickupTime(pickupTimeFrom, intl) : null;
  const to = pickupTimeTo ? formatPickupTime(pickupTimeTo, intl) : null;
  return from && to
    ? intl.formatMessage({ id: 'PickupDetails.timeWindow' }, { from, to })
    : from
    ? intl.formatMessage({ id: 'PickupDetails.timeWindowFrom' }, { from })
    : to
    ? intl.formatMessage({ id: 'PickupDetails.timeWindowTo' }, { to })
    : null;
};
